import apiClient from "./api";
import { getApiAccessToken } from "./UserService";

const baseUrl = "/api/CityObjectSelfDelivery";

export interface SelfDeliveryCheckItem {
  lookupDataId: number;
  description: string;
  valueChecked: boolean;
  deviation: string | null;
  correctiveAction: string | null;
  responsiblePerson: string | null;
  information: string | null;
}

export interface SelfDeliverySubObject {
  id: number;
  name: string | null;
  createdDate: string | null;
  includeInSelfDelivery: boolean;
}

export interface SelfDeliveryComment {
  id: number;
  created: string;
  creatorName: string | null;
  commentText: string | null;
}

export interface SelfDelivery {
  id: number;
  objectId: number;
  contractId: number;
  selfDeliveryStatus: number; // 0 draft, 1 submitted, 2 approved
  location: string | null;
  clientName: string | null;
  modifierName: string | null;
  acceptedByName: string | null;
  lastModified: string | null;
  acceptedDate: string | null;
  pavementInformation: string | null;
  streetPermitCost: number | null;
  otherCost: number | null;
  otherCostInformation: string | null;
  roadMarkingInformation: string | null;
  subObjectInformation: string | null;
  areaChecked: boolean;
  scheduleChecked: boolean;
  materialsChecked: boolean;
  checkInformation: string | null;
  deviationInformation: string | null;
  canModify: boolean;
  reviewChecks: SelfDeliveryCheckItem[];
  pavementChecks: SelfDeliveryCheckItem[];
  subObjects: SelfDeliverySubObject[];
  comments: SelfDeliveryComment[];
}

export interface SaveSelfDeliveryCheckValue {
  lookupDataId: number;
  valueChecked: boolean;
  deviation: string | null;
  correctiveAction: string | null;
  responsiblePerson: string | null;
  information: string | null;
}

export interface SaveSelfDeliveryRequest {
  objectId: number;
  contractId: number;
  userId: number;
  addressSystemId: number;
  saveFinal: boolean;
  pavementInformation: string | null;
  streetPermitCost: number | null;
  otherCost: number | null;
  otherCostInformation: string | null;
  roadMarkingInformation: string | null;
  subObjectInformation: string | null;
  areaChecked: boolean;
  scheduleChecked: boolean;
  materialsChecked: boolean;
  checkInformation: string | null;
  deviationInformation: string | null;
  subObjectIds: number[];
  checkValues: SaveSelfDeliveryCheckValue[];
}

export interface SelfDeliveryWorkflowRequest {
  id: number;
  objectId: number;
  contractId: number;
  userId: number;
  addressSystemId: number;
}

export interface AddSelfDeliveryCommentRequest {
  id: number;
  objectId: number;
  contractId: number;
  userId: number;
  addressSystemId: number;
  commentText: string;
}

const authHeaders = async () => {
  const token = await getApiAccessToken();
  return { headers: { Authorization: `Bearer ${token}` } };
};

/** Load the full Self Delivery payload for an object + contract. */
export const getSelfDelivery = async (
  objectId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<SelfDelivery> => {
  const token = await getApiAccessToken();
  const response = await apiClient.get<SelfDelivery>(`${baseUrl}/${objectId}`, {
    params: { contractId, userId, addressSystemId },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

/** Save the form as a draft or submit for approval. Returns the record id. */
export const saveSelfDelivery = async (
  request: SaveSelfDeliveryRequest,
): Promise<number> => {
  const response = await apiClient.post<{ id: number }>(
    `${baseUrl}/save`,
    request,
    await authHeaders(),
  );
  return response.data.id;
};

/** Approve a submitted self delivery (status 1 → 2). */
export const approveSelfDelivery = async (
  request: SelfDeliveryWorkflowRequest,
): Promise<void> => {
  await apiClient.post(`${baseUrl}/approve`, request, await authHeaders());
};

/** Release a self delivery back to draft (status → 0). */
export const releaseSelfDelivery = async (
  request: SelfDeliveryWorkflowRequest,
): Promise<void> => {
  await apiClient.post(`${baseUrl}/release`, request, await authHeaders());
};

/** Add a comment to a self delivery record. */
export const addSelfDeliveryComment = async (
  request: AddSelfDeliveryCommentRequest,
): Promise<SelfDeliveryComment> => {
  const response = await apiClient.post<SelfDeliveryComment>(
    `${baseUrl}/comment`,
    request,
    await authHeaders(),
  );
  return response.data;
};
